import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Page } from "./components/Page";
import { gameDefinitions } from "./games/gameDefinitons";
import { interactiveDefinitions } from "./interactives/interactiveDefinitions";
import { GameList } from "./games/GameList";
import { InteractiveList } from "./interactives/InteractiveList";
import { Home } from "./home/Home";
import { toolDefinitions } from "./tools/toolDefinitions";
import { ToolList } from "./tools/ToolList";
import ToolSite from "./tools/ToolSite";
import GameSite from "./games/GameSite";
import InteractiveSite from "./interactives/InteractiveSite";
import ProjectSite from "./projects/ProjectSite";
import { ProjectList } from "./projects/ProjectList";
import { AuthGuard } from "./auth/AuthGuard";
import { AdminGuard } from "./admin/AdminGuard";
import { EmailBanner } from "./auth/components/EmailBanner";
import CompetitionSite from "./competitions/CompetitionSite";
import { CompetitionList } from "./competitions/CompetitionList";
import { competitionDefinitions } from "./competitions/competitionDefinitions";
import FortuneSite from "./fortune/FortuneSite";
import { LegacyFortuneRedirect } from "./fortune/LegacyFortuneRedirect";
import { FortuneList } from "./fortune/FortuneList";
import { fortuneDefinitions } from "./fortune/fortuneDefinitions";

const ScottBrownPage = lazy(() => import("./pages/ScottBrownPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const LoginPage = lazy(() => import("./auth/components/LoginPage"));
const SignupPage = lazy(() => import("./auth/components/SignupPage"));
const ForgotPasswordPage = lazy(
  () => import("./auth/components/ForgotPasswordPage")
);
const ResetPasswordPage = lazy(
  () => import("./auth/components/ResetPasswordPage")
);
const VerifyEmailPage = lazy(() => import("./auth/components/VerifyEmailPage"));
const SettingsPage = lazy(() => import("./auth/components/SettingsPage"));
const AdminSite = lazy(() => import("./admin/AdminSite"));
const AdminOverviewPage = lazy(() => import("./admin/AdminOverviewPage"));
const AdminUsersPage = lazy(() => import("./admin/AdminUsersPage"));
const AdminLobbiesPage = lazy(() => import("./admin/AdminLobbiesPage"));
const AdminHealthPage = lazy(() => import("./admin/AdminHealthPage"));
const AdminJobsPage = lazy(() => import("./admin/AdminJobsPage"));
const AdminToolsPage = lazy(() => import("./admin/AdminToolsPage"));
const AdminInteractivesPage = lazy(
  () => import("./admin/AdminInteractivesPage")
);
const AdminGamesPage = lazy(() => import("./admin/AdminGamesPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const CardPreview = import.meta.env.DEV
  ? lazy(() => import("./fortune/cards/CardPreview"))
  : null;

export function PageRoutes() {
  return (
    <>
      <EmailBanner />
      <Routes>
        {CardPreview && <Route path="/dev/cards" element={<CardPreview />} />}
        <Route path="/" element={<Home />} />
        <Route path="/scott-brown" element={<ScottBrownPage />} />
        <Route
          path="/games/simPlanets"
          element={<Navigate to="/interactives/simPlanets" replace />}
        />
        <Route path="/games" element={<GameSite />}>
          <Route index element={<GameList />} />
          {gameDefinitions.map((gameDefinition) => {
            const Game = gameDefinition.component;
            return (
              <Route
                key={gameDefinition.name}
                path={gameDefinition.name}
                element={
                  gameDefinition.fullscreen ? (
                    <Game />
                  ) : (
                    <Page
                      title={gameDefinition.title}
                      description={gameDefinition.desc}
                      ogImage={gameDefinition.image}
                      backTo="/games"
                    >
                      <Game />
                    </Page>
                  )
                }
              />
            );
          })}
        </Route>
        <Route path="/games-of-chance/*" element={<LegacyFortuneRedirect />} />
        <Route
          path="/fortune/melbourne-cup-sweep"
          element={<LegacyFortuneRedirect />}
        />
        <Route path="/fortune" element={<FortuneSite />}>
          <Route index element={<FortuneList />} />
          {fortuneDefinitions.map((definition) => {
            const FortuneGame = definition.component;
            return (
              <Route
                key={definition.name}
                path={definition.name}
                element={
                  <Page
                    title={definition.title}
                    description={definition.description}
                    ogImage={definition.image}
                    backTo="/fortune"
                  >
                    <FortuneGame />
                  </Page>
                }
              />
            );
          })}
        </Route>
        <Route path="/competitions" element={<CompetitionSite />}>
          <Route index element={<CompetitionList />} />
          {competitionDefinitions.map((definition) => {
            const Competition = definition.component;
            return (
              <Route
                key={definition.name}
                path={definition.name}
                element={
                  <Page
                    title={definition.title}
                    description={definition.description}
                    ogImage={definition.image}
                    backTo="/competitions"
                  >
                    <Competition />
                  </Page>
                }
              />
            );
          })}
        </Route>
        <Route path="/interactives" element={<InteractiveSite />}>
          <Route index element={<InteractiveList />} />
          {interactiveDefinitions.map((def) => {
            const Interactive = def.component;
            return (
              <Route
                key={def.name}
                path={def.name}
                element={
                  def.fullscreen ? (
                    <Interactive />
                  ) : (
                    <Page
                      title={def.title}
                      description={def.desc}
                      ogImage={def.image}
                      backTo="/interactives"
                    >
                      <Interactive />
                    </Page>
                  )
                }
              />
            );
          })}
        </Route>
        <Route path="/tools" element={<ToolSite />}>
          <Route index element={<ToolList />} />
          {toolDefinitions.map((toolDefinition) => {
            const Tool = toolDefinition.component;
            return (
              <Route
                key={toolDefinition.name}
                path={toolDefinition.name}
                element={
                  <Page
                    title={toolDefinition.title}
                    description={toolDefinition.desc}
                    backTo="/tools"
                  >
                    <Tool />
                  </Page>
                }
              />
            );
          })}
        </Route>
        <Route path="/projects" element={<ProjectSite />}>
          <Route index element={<ProjectList />} />
        </Route>
        <Route
          path="/privacy"
          element={
            <Page title="Privacy Policy" backTo="/">
              <PrivacyPolicy />
            </Page>
          }
        />
        <Route
          path="/terms"
          element={
            <Page title="Terms of Use" backTo="/">
              <TermsOfUse />
            </Page>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <SettingsPage />
            </AuthGuard>
          }
        />
        <Route
          element={
            <AdminGuard>
              <AdminSite />
            </AdminGuard>
          }
        >
          <Route path="/admin" element={<AdminOverviewPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/lobbies" element={<AdminLobbiesPage />} />
          <Route path="/admin/health" element={<AdminHealthPage />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/games" element={<AdminGamesPage />} />
          <Route path="/admin/tools" element={<AdminToolsPage />} />
          <Route
            path="/admin/interactives"
            element={<AdminInteractivesPage />}
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
